<template><div><dt-notice
  kind="warning"
  :hideClose="true"
  class="d-wmx100p"
>
  <template #default>
    <p class="d-body--md-compact">Before applying a typography utility, first consider using <dt-link to="/design/typography/" kind="muted" class="d-fw-bold">Dialtone's text styles</dt-link> that bundles Font family, Font weight, Font size, and Line height together.</p>
  </template>
</dt-notice>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-{fs|headline}-{stop}</code> to change an element's font-size in the product or <code v-pre>d-{fs|headline}-{stop}-{platform}</code> to
change in other platforms (mobile, tc8, tv).</p>
<code-well-header>
  <div class="d-d-grid d-g16 d-ai-center" style="grid-template-columns: 10rem 1fr">
    <div class="d-code--sm d-docsite-code">.d-fs-100</div>
    <div><p class="d-fs-100">The quick brown fox jumps over the lazy dog.</p></div>
    <div class="d-code--sm d-docsite-code">.d-fs-200</div>
    <div><p class="d-fs-200">The quick brown fox jumps over the lazy dog.</p></div>
    <div class="d-code--sm d-docsite-code">.d-fs-300</div>
    <div><p class="d-fs-300">The quick brown fox jumps over the lazy dog.</p></div>
    <div class="d-code--sm d-docsite-code">.d-fs-400</div>
    <div><p class="d-fs-400">The quick brown fox jumps over the lazy dog.</p></div>
    <div class="d-code--sm d-docsite-code">.d-fs-500</div>
    <div><p class="d-fs-500">The quick brown fox jumps over the lazy dog.</p></div>
  </div>
</code-well-header>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-fs-100<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>...<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-fs-200<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>...<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-fs-300<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>...<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-fs-400<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>...<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-fs-500<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>...<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h2 id="classes-by-platform" tabindex="-1" class="d-docsite--header-2">Classes by Platform <a class="header-anchor d-link d-docsite--link d-link" href="#classes-by-platform" aria-hidden="true">#</a></h2>
<h3 id="product" tabindex="-1" class="d-docsite--header-3">Product <a class="header-anchor d-link d-docsite--link d-link" href="#product" aria-hidden="true">#</a></h3>
<clamped-table-wrapper>
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-t0">
        <tr>
          <th scope="col" class="d-p0 d-bbw0 d-w10p"><div class="d-p16 d-bb d-bc-default d-bbw1">Size</div></th>
          <th scope="col" class="d-p0 d-bbw0 d-w20p"><div class="d-p16 d-bb d-bc-default d-bbw1">Class</div></th>
          <th scope="col" class="d-p0 d-bbw0"><div class="d-p16 d-bb d-bc-default d-bbw1">Output</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="{ stop, size, output } in fontSize.product">
          <th scope="row">{{ size }}px</th>
          <td class="d-code--sm d-docsite-code">.d-fs-{{ stop }}</td>
          <td>
            <dt-stack direction="row" justify="between" align="center">
              <div class="d-fl1 d-code--sm">
                font-size: {{ output }}rem !important;
              </div>
              <div class="d-fl0 d-lh4" :class="`d-fs-${stop}`">
                Aa
              </div>
            </dt-stack>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
<h3 id="mobile" tabindex="-1" class="d-docsite--header-3">Mobile <a class="header-anchor d-link d-docsite--link d-link" href="#mobile" aria-hidden="true">#</a></h3>
<clamped-table-wrapper>
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-t0">
        <tr>
          <th scope="col" class="d-p0 d-bbw0 d-w10p"><div class="d-p16 d-bb d-bc-default d-bbw1">Size</div></th>
          <th scope="col" class="d-p0 d-bbw0 d-w20p"><div class="d-p16 d-bb d-bc-default d-bbw1">Class</div></th>
          <th scope="col" class="d-p0 d-bbw0"><div class="d-p16 d-bb d-bc-default d-bbw1">Output</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="{ stop, size, output } in fontSize.mobile">
          <th scope="row">{{ size }}px</th>
          <td class="d-code--sm d-docsite-code">.d-fs-{{ stop }}-mobile</td>
          <td>
            <dt-stack direction="row" justify="between" align="center">
              <div class="d-fl1 d-code--sm">
                font-size: {{ output }}rem !important;
              </div>
              <div class="d-fl0 d-lh4" :class="`d-fs-${stop}-mobile`">
                Aa
              </div>
            </dt-stack>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
<h3 id="tc8" tabindex="-1" class="d-docsite--header-3">TC8 <a class="header-anchor d-link d-docsite--link d-link" href="#tc8" aria-hidden="true">#</a></h3>
<clamped-table-wrapper>
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-t0">
        <tr>
          <th scope="col" class="d-p0 d-bbw0 d-w10p"><div class="d-p16 d-bb d-bc-default d-bbw1">Size</div></th>
          <th scope="col" class="d-p0 d-bbw0 d-w20p"><div class="d-p16 d-bb d-bc-default d-bbw1">Class</div></th>
          <th scope="col" class="d-p0 d-bbw0"><div class="d-p16 d-bb d-bc-default d-bbw1">Output</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="{ stop, size, output } in fontSize.tc8">
          <th scope="row">{{ size }}px</th>
          <td class="d-code--sm d-docsite-code">.d-fs-{{ stop }}-tc8</td>
          <td>
            <dt-stack direction="row" justify="between" align="center">
              <div class="d-fl1 d-code--sm">
                font-size: {{ output }}rem !important;
              </div>
              <div class="d-fl0 d-lh4" :class="`d-fs-${stop}-tc8`">
                Aa
              </div>
            </dt-stack>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
<h3 id="tv" tabindex="-1" class="d-docsite--header-3">TV <a class="header-anchor d-link d-docsite--link d-link" href="#tv" aria-hidden="true">#</a></h3>
<clamped-table-wrapper>
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-t0">
        <tr>
          <th scope="col" class="d-p0 d-bbw0 d-w10p"><div class="d-p16 d-bb d-bc-default d-bbw1">Size</div></th>
          <th scope="col" class="d-p0 d-bbw0 d-w20p"><div class="d-p16 d-bb d-bc-default d-bbw1">Class</div></th>
          <th scope="col" class="d-p0 d-bbw0"><div class="d-p16 d-bb d-bc-default d-bbw1">Output</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="{ stop, size, output } in fontSize.tv">
          <th scope="row">{{ size }}px</th>
          <td class="d-code--sm d-docsite-code">.d-fs-{{ stop }}-tv</td>
          <td>
            <dt-stack direction="row" justify="between" align="center">
              <div class="d-fl1 d-code--sm">
                font-size: {{ output }}rem !important;
              </div>
              <div class="d-fl0 d-lh4" :class="`d-fs-${stop}-tv`">
                Aa
              </div>
            </dt-stack>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
</div></template>


<script setup>
  import { fontSize } from '@data/type.json';
  import ClampedTableWrapper from '@baseComponents/ClampedTableWrapper.vue';
</script>